// Bible data structure and utilities for the Family Audio Bible app

// Import the parsed Bible data (World English Bible is the default, bundled).
import bibleData from '../data/bibleData.json';
import searchIndex from '../data/searchIndex.json';
import stats from '../data/stats.json';

// ---------------------------------------------------------------------------
// Translations
// ---------------------------------------------------------------------------
// The app can display/record from more than one translation. The WEB text is
// bundled (default); additional translations are lazy-loaded on demand so they
// don't bloat the initial bundle. All translations share the same nested shape:
//   data[bookId][chapter][verse] = "text"
export const TRANSLATIONS = [
  { id: 'web', name: 'World English Bible', abbreviation: 'WEB' },
  { id: 'bsb', name: 'Berean Standard Bible', abbreviation: 'BSB' },
];

export const DEFAULT_TRANSLATION = 'web';

const translationCache = { web: bibleData };

// The dataset the read functions below operate on. Defaults to WEB.
let activeData = bibleData;
let activeTranslationId = 'web';

export function getActiveTranslationId() {
  return activeTranslationId;
}

export function getTranslationInfo(id) {
  return TRANSLATIONS.find((t) => t.id === id) || TRANSLATIONS[0];
}

// Switch the active translation, lazy-loading its data the first time.
// Returns the id that actually became active (falls back to 'web' on error).
export async function setTranslation(id) {
  if (!TRANSLATIONS.find((t) => t.id === id)) id = DEFAULT_TRANSLATION;

  if (!translationCache[id]) {
    try {
      if (id === 'bsb') {
        const mod = await import('../data/bibleDataBSB.json');
        translationCache.bsb = mod.default || mod;
      }
    } catch (e) {
      console.error('Failed to load translation', id, e);
      id = DEFAULT_TRANSLATION;
    }
  }

  activeData = translationCache[id] || bibleData;
  activeTranslationId = id;
  return activeTranslationId;
}

// Bible books with their canonical order and chapter counts
export const BIBLE_BOOKS = [
  { id: 'genesis', name: 'Genesis', chapters: 50 },
  { id: 'exodus', name: 'Exodus', chapters: 40 },
  { id: 'leviticus', name: 'Leviticus', chapters: 27 },
  { id: 'numbers', name: 'Numbers', chapters: 36 },
  { id: 'deuteronomy', name: 'Deuteronomy', chapters: 34 },
  { id: 'joshua', name: 'Joshua', chapters: 24 },
  { id: 'judges', name: 'Judges', chapters: 21 },
  { id: 'ruth', name: 'Ruth', chapters: 4 },
  { id: '1-samuel', name: '1 Samuel', chapters: 31 },
  { id: '2-samuel', name: '2 Samuel', chapters: 24 },
  { id: '1-kings', name: '1 Kings', chapters: 22 },
  { id: '2-kings', name: '2 Kings', chapters: 25 },
  { id: '1-chronicles', name: '1 Chronicles', chapters: 29 },
  { id: '2-chronicles', name: '2 Chronicles', chapters: 36 },
  { id: 'ezra', name: 'Ezra', chapters: 10 },
  { id: 'nehemiah', name: 'Nehemiah', chapters: 13 },
  { id: 'esther', name: 'Esther', chapters: 10 },
  { id: 'job', name: 'Job', chapters: 42 },
  { id: 'psalms', name: 'Psalms', chapters: 150 },
  { id: 'proverbs', name: 'Proverbs', chapters: 31 },
  { id: 'ecclesiastes', name: 'Ecclesiastes', chapters: 12 },
  { id: 'song-of-solomon', name: 'Song of Solomon', chapters: 8 },
  { id: 'isaiah', name: 'Isaiah', chapters: 66 },
  { id: 'jeremiah', name: 'Jeremiah', chapters: 52 },
  { id: 'lamentations', name: 'Lamentations', chapters: 5 },
  { id: 'ezekiel', name: 'Ezekiel', chapters: 48 },
  { id: 'daniel', name: 'Daniel', chapters: 12 },
  { id: 'hosea', name: 'Hosea', chapters: 14 },
  { id: 'joel', name: 'Joel', chapters: 3 },
  { id: 'amos', name: 'Amos', chapters: 9 },
  { id: 'obadiah', name: 'Obadiah', chapters: 1 },
  { id: 'jonah', name: 'Jonah', chapters: 4 },
  { id: 'micah', name: 'Micah', chapters: 7 },
  { id: 'nahum', name: 'Nahum', chapters: 3 },
  { id: 'habakkuk', name: 'Habakkuk', chapters: 3 },
  { id: 'zephaniah', name: 'Zephaniah', chapters: 3 },
  { id: 'haggai', name: 'Haggai', chapters: 2 },
  { id: 'zechariah', name: 'Zechariah', chapters: 14 },
  { id: 'malachi', name: 'Malachi', chapters: 4 },
  { id: 'matthew', name: 'Matthew', chapters: 28 },
  { id: 'mark', name: 'Mark', chapters: 16 },
  { id: 'luke', name: 'Luke', chapters: 24 },
  { id: 'john', name: 'John', chapters: 21 },
  { id: 'acts', name: 'Acts', chapters: 28 },
  { id: 'romans', name: 'Romans', chapters: 16 },
  { id: '1-corinthians', name: '1 Corinthians', chapters: 16 },
  { id: '2-corinthians', name: '2 Corinthians', chapters: 13 },
  { id: 'galatians', name: 'Galatians', chapters: 6 },
  { id: 'ephesians', name: 'Ephesians', chapters: 6 },
  { id: 'philippians', name: 'Philippians', chapters: 4 },
  { id: 'colossians', name: 'Colossians', chapters: 4 },
  { id: '1-thessalonians', name: '1 Thessalonians', chapters: 5 },
  { id: '2-thessalonians', name: '2 Thessalonians', chapters: 3 },
  { id: '1-timothy', name: '1 Timothy', chapters: 6 },
  { id: '2-timothy', name: '2 Timothy', chapters: 4 },
  { id: 'titus', name: 'Titus', chapters: 3 },
  { id: 'philemon', name: 'Philemon', chapters: 1 },
  { id: 'hebrews', name: 'Hebrews', chapters: 13 },
  { id: 'james', name: 'James', chapters: 5 },
  { id: '1-peter', name: '1 Peter', chapters: 5 },
  { id: '2-peter', name: '2 Peter', chapters: 3 },
  { id: '1-john', name: '1 John', chapters: 5 },
  { id: '2-john', name: '2 John', chapters: 1 },
  { id: '3-john', name: '3 John', chapters: 1 },
  { id: 'jude', name: 'Jude', chapters: 1 },
  { id: 'revelation', name: 'Revelation', chapters: 22 }
];

// Parse reference string (e.g., "Genesis 1:1") into components
export function parseReference(reference) {
  const match = reference.match(/^(.+?)\s+(\d+):(\d+)$/);
  if (!match) return null;
  
  const [, bookName, chapter, verse] = match;
  return {
    bookName: bookName.trim(),
    chapter: parseInt(chapter),
    verse: parseInt(verse)
  };
}

// Get book ID from book name
export function getBookId(bookName) {
  const book = BIBLE_BOOKS.find(b => 
    b.name.toLowerCase() === bookName.toLowerCase() ||
    b.id.replace('-', ' ') === bookName.toLowerCase()
  );
  return book ? book.id : null;
}

// Get book info by ID
export function getBookInfo(bookId) {
  return BIBLE_BOOKS.find(b => b.id === bookId);
}

// Get chapters for a book
export function getChaptersForBook(bookId) {
  const book = getBookInfo(bookId);
  if (!book) return [];
  
  return Array.from({ length: book.chapters }, (_, i) => i + 1);
}

// Get verses for a chapter from the active translation's data
export function getVersesForChapter(bookId, chapter) {
  if (!activeData[bookId] || !activeData[bookId][chapter]) {
    return [];
  }

  return Object.keys(activeData[bookId][chapter])
    .map(verse => parseInt(verse))
    .sort((a, b) => a - b);
}

// Search books by name
export function searchBooks(query) {
  const searchTerm = query.toLowerCase();
  return BIBLE_BOOKS.filter(book => 
    book.name.toLowerCase().includes(searchTerm) ||
    book.id.toLowerCase().includes(searchTerm)
  );
}

// Get books by testament
export function getBooksByTestament() {
  const oldTestament = BIBLE_BOOKS.slice(0, 39);
  const newTestament = BIBLE_BOOKS.slice(39);
  
  return {
    oldTestament,
    newTestament
  };
}

// Format reference for display
export function formatReference(bookName, chapter, verse) {
  return `${bookName} ${chapter}:${verse}`;
}

// Get verse text from the active translation's data
export function getVerseText(bookId, chapter, verse) {
  if (!activeData[bookId] || !activeData[bookId][chapter] || !activeData[bookId][chapter][verse]) {
    return `Verse not found: ${bookId} ${chapter}:${verse}`;
  }

  return activeData[bookId][chapter][verse];
}

// Search verses by text content
export function searchVerses(query, limit = 20) {
  const searchTerm = query.toLowerCase();
  const results = [];
  
  Object.keys(searchIndex).forEach(key => {
    const verse = searchIndex[key];
    if (verse.text.toLowerCase().includes(searchTerm)) {
      results.push({
        ...verse,
        bookName: getBookInfo(verse.bookId)?.name || verse.bookId
      });
    }
  });
  
  return results.slice(0, limit);
}

// Get random verse
export function getRandomVerse() {
  const keys = Object.keys(searchIndex);
  const randomKey = keys[Math.floor(Math.random() * keys.length)];
  const verse = searchIndex[randomKey];
  
  return {
    ...verse,
    bookName: getBookInfo(verse.bookId)?.name || verse.bookId
  };
}

// Get Bible statistics
export function getBibleStats() {
  return stats;
}

// Export the raw Bible data for advanced usage
export { bibleData, searchIndex }; 